/**
 * THE single definition of the Falcon mark.
 *
 * Traced from public/brand/falcon-logo.jpg by measuring the source pixels
 * (scripts/trace-logo.mjs), not by eye and not by auto-trace.
 *
 * Every use of the mark on this site — nav lockup, footer, favicon, watermark,
 * OG image — renders THIS path. Nothing imports a different one, and no variant
 * is redrawn per context. Uses differ only in scale, color and opacity, so the
 * mark cannot drift out of sync with itself.
 *
 * DO NOT EDIT the geometry. It reproduces the client's existing logo, and
 * altering it changes their brand identity. See the fidelity rule in the plan.
 *
 * Measured vertices from source (200x200 JPEG, bbox 113x110 -> aspect 1.0273),
 * normalized to a 1000 x 973 space (scripts/extract-outline.mjs):
 *
 *   stem          left edge x=8.8, constant for the full height
 *   upper arm     top edge y=0 spans 8.8..991.2; lower edge runs from
 *                 (991.2, 0) to (513.3, 212.3) and is subtly CONVEX --
 *                 it bulges up to 33.2 units right of a straight chord,
 *                 ~4x the 8.85-unit row height, so this is real curvature
 *                 in the mark, not pixel quantization. Preserved with a
 *                 quadratic curve; flattening it would alter the logo.
 *   arm gap       y 221.1 .. 327.3, stem only (right edge 212.4)
 *   lower arm     top edge y=336.1 spans to 796.5; lower edge from
 *                 (796.5, 336.1) to (230.1, 548.4), convex by up to 19.6
 *   lower stem    y 557.3 .. 672.3, right edge 212.4
 *   fork          splits at y=681.1 into two prongs:
 *                   left  prong: outer edge x=8.8 vertical, inner edge
 *                                slopes 97.3 -> 8.8, tip at y~946.5
 *                   right prong: inner edge slopes 115 -> 212.4,
 *                                outer edge x=212.4, tip at y~964.2
 */

/** viewBox for the mark. Matches the measured 113:110 aspect ratio. */
export const MARK_VIEWBOX = '0 0 1000 973';

/**
 * The mark, in a 1000-unit-wide coordinate space.
 * Traced clockwise from the stem's top-left corner.
 */
export const MARK_PATH =
  'M8.8 0 ' +
  'L1000 0 ' +                    // top edge, full width
  'Q778.8 106.1 522.1 212.3 ' +   // upper arm's convex underside to its point
  'L221.2 212.3 ' +               // across to the stem's right edge
  'L221.2 336.1 ' +               // down the stem through the arm gap
  'L805.3 336.1 ' +               // lower arm's top edge
  'Q663.7 429 451.3 539.6 ' +     // lower arm's convex underside
  'L221.2 557.3 ' +               // settle onto the stem's right edge
  'L221.2 973 ' +                 // down the stem to the right prong's tip
  'L115 681.1 ' +                 // up the right prong's inner edge into the notch
  'L106.2 681.1 ' +               // across the notch floor
  'L8.8 955.3 ' +                 // down the left prong's inner edge to its tip
  'Z';

/**
 * The mark's blue, sampled from falcon-logo.jpg (dominant pixel value).
 * Note this differs slightly from the banner's primary blue (#1E68C2) --
 * the two source assets genuinely use different blues.
 */
export const MARK_BLUE = '#145FBC';

/**
 * Fidelity, measured by two independent checks (see scripts/):
 *   verify-logo.mjs    99.06% antialias-aware pixel agreement
 *   check-vertices.mjs 11/11 landmarks within one source pixel (max 0.05u)
 * Re-run both after ANY change to MARK_PATH.
 */
